import React, { useRef, useState } from "react";

import { useWebDashboards } from "../../api";
import { MemoryIcon, SignOutIcon } from "../../components";

import { DashboardHelpMenu } from "./DashboardHelpMenu";
import { MyDashboardsPopover } from "./MyDashboardsPopover";
import { WebDashboardNavIconHelp, WebDashboardNavIconMyDashboards } from "./WebDashboardNavIcons";

interface UserMenuDropdownProps {
  name?: string;
  email: string;
  isAuthenticated: boolean;
  /** Whether the help menu shows the editor-only options (Schedule a demo, Watch how-to video). */
  showHelpExtraOptions: boolean;
  /** Opens the Memory modal (parent owns the modal + closes this menu). */
  onMemoryClick?: () => void;
  onSupportFeedbackClick: () => void;
  onHelpVideoClick: () => void;
  onLogoutClick: () => void;
  /** Closes the entire user menu (e.g. when the My Dashboards popover is dismissed). */
  onClose: () => void;
}

export const UserMenuDropdown: React.FC<UserMenuDropdownProps> = ({
  name,
  email,
  isAuthenticated,
  showHelpExtraOptions,
  onMemoryClick,
  onSupportFeedbackClick,
  onHelpVideoClick,
  onLogoutClick,
  onClose,
}) => {
  const initial = (name || email).charAt(0).toUpperCase();

  const [showMyDashboards, setShowMyDashboards] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const helpMenuRef = useRef<HTMLDivElement>(null);
  const { data: dashboards, isLoading } = useWebDashboards({ enabled: isAuthenticated });

  return (
    <div className="relative">
      {/* My Dashboards popover — replaces the menu once "My Dashboards" is clicked */}
      <MyDashboardsPopover
        visible={showMyDashboards}
        dashboards={dashboards}
        isLoading={isLoading}
        dropdownRef={popoverRef}
        onClose={() => {
          setShowMyDashboards(false);
          onClose();
        }}
      />

      {!showMyDashboards && (
        <div className="themed-dropdown-menu flex flex-col gap-3 items-start py-5 rounded-xl shadow-[0_0_16px_rgba(0,0,0,0.08),0_16px_24px_rgba(0,0,0,0.08)] w-max overflow-visible">
          {/* User info */}
          <div className="flex gap-2.5 items-start px-5">
            <div className="inline-flex items-center justify-center rounded-full size-8 shrink-0 bg-primary-button-background/50">
              <span className="text-sm font-semibold leading-[18px] text-white">{initial}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-sm text-main-foreground leading-[18px] whitespace-nowrap">
                {name}
              </span>
              <span className="text-xs text-main-muted-foreground leading-[18px] whitespace-nowrap">
                {email}
              </span>
            </div>
          </div>

          {/* Menu items — no gap so hover/clickable areas are flush; padding lives on the items */}
          <div className="flex flex-col items-start w-full">
            {/* Divider, kept inset via a px-5 wrapper; mb separates it from the first item */}
            <div className="w-full px-5 mb-2.5">
              <div className="border-t border-main-border" />
            </div>

            {!isLoading && dashboards && dashboards.length >= 1 && (
              <button
                type="button"
                className="themed-dropdown-item flex gap-1.5 items-center w-full px-5 py-2 text-left cursor-pointer transition-colors hover:bg-main-text-button-hover-background"
                onClick={() => {
                  setShowHelpMenu(false);
                  setShowMyDashboards(true);
                }}
              >
                <span className="flex items-center justify-center size-6 shrink-0">
                  <WebDashboardNavIconMyDashboards />
                </span>
                <span className="text-sm text-main-foreground leading-[18px] whitespace-nowrap">
                  My Dashboards
                </span>
              </button>
            )}

            {onMemoryClick && (
              <button
                type="button"
                className="themed-dropdown-item flex gap-1.5 items-center w-full px-5 py-2 text-left cursor-pointer transition-colors hover:bg-main-text-button-hover-background"
                onClick={() => {
                  setShowHelpMenu(false);
                  onMemoryClick();
                }}
              >
                <span className="flex items-center justify-center size-6 shrink-0">
                  <MemoryIcon width="16px" height="16px" />
                </span>
                <span className="text-sm text-main-foreground leading-[18px] whitespace-nowrap">
                  Memory
                </span>
              </button>
            )}

            {/* Relative wrapper so the help menu's top edge aligns with the Help button row */}
            <div className="relative w-full">
              <button
                type="button"
                className="flex gap-1.5 items-center w-full px-5 py-2 text-left cursor-pointer transition-colors hover:bg-main-text-button-hover-background"
                onClick={() => setShowHelpMenu(v => !v)}
              >
                <span className="flex items-center justify-center size-6 shrink-0">
                  <WebDashboardNavIconHelp />
                </span>
                <span className="text-sm text-main-foreground leading-[18px] whitespace-nowrap">
                  Help
                </span>
              </button>

              {/* Help menu — flies out to the left */}
              <DashboardHelpMenu
                visible={showHelpMenu}
                dropdownRef={helpMenuRef}
                showExtraOptions={showHelpExtraOptions}
                onClose={() => setShowHelpMenu(false)}
                onSupportFeedbackClick={onSupportFeedbackClick}
                onHelpVideoClick={onHelpVideoClick}
              />
            </div>

            <button
              type="button"
              className="flex gap-1.5 items-center w-full px-5 py-2 text-left cursor-pointer transition-colors hover:bg-main-text-button-hover-background"
              onClick={onLogoutClick}
            >
              <span className="flex items-center justify-center size-6 shrink-0">
                <SignOutIcon fill="currentColor" />
              </span>
              <span className="text-sm text-main-foreground leading-[18px] whitespace-nowrap">
                Log out
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
